<script lang="ts">
export interface TopListing {
  rank: number
  ticker: string
  projectLogo: string | null
  exchangeName: string
  day1VolumeUsd: number
  // Percentage value (26.4 = +26.4%) — formatted with `percent:1`.
  pricePerformance7d: number
  // Sparkline series for ChartSparkline (raw relative values).
  trend: Array<{ x: number, y: number }>
}
</script>

<script lang="ts" setup>
// Dual <script> blocks for the exported TopListing type; setup imports follow.
/* eslint-disable import/first */
import { formatSignedPercent } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Badge } from '@forgd/ui-kit/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'

defineProps<{
  title: string
  listings: TopListing[]
  loading?: boolean
}>()

// Row count for the loading state — keeps skeleton height matched to the
// loaded table (which renders 3 ranked rows).
const SKELETON_ROW_COUNT = 3

const SPARKLINE_UP_COLOR = '#16a34a'
const SPARKLINE_DOWN_COLOR = '#dc2626'
</script>

<template>
  <Card>
    <CardHeader class="flex flex-row items-center justify-between border-b border-default">
      <CardTitle>{{ title }}</CardTitle>
      <Badge variant="gray">
        Past 30 Days
      </Badge>
    </CardHeader>
    <CardContent class="p-4">
      <div class="flex flex-col divide-y divide-default rounded-lg border border-default">
        <template v-if="loading">
          <div
            v-for="n in SKELETON_ROW_COUNT"
            :key="`skeleton-${n}`"
            class="flex flex-col gap-3 p-4 sm:grid sm:grid-cols-[20px_minmax(0,1fr)_auto] sm:items-center sm:gap-2"
          >
            <div class="flex items-center gap-2 sm:contents">
              <Skeleton class="size-4 shrink-0" />
              <div class="flex min-w-0 items-center gap-2">
                <Skeleton class="size-8 shrink-0 rounded-full" />
                <div class="flex min-w-0 flex-col gap-1">
                  <Skeleton class="h-4 w-16" />
                  <Skeleton class="h-3 w-40" />
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between gap-2 sm:flex-col sm:items-end sm:gap-1">
              <Skeleton class="h-3 w-28" />
              <div class="inline-flex items-center gap-2">
                <Skeleton class="h-4 w-12" />
                <Skeleton class="h-6 w-16 shrink-0" />
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="listing in listings"
            :key="listing.rank"
            class="flex flex-col gap-3 p-4 sm:grid sm:grid-cols-[20px_minmax(0,1fr)_auto] sm:items-center sm:gap-2"
          >
            <div class="flex items-center gap-2 sm:contents">
              <div class="text-center text-xs font-semibold text-primal">
                {{ listing.rank }}
              </div>
              <div class="flex min-w-0 items-center gap-2">
                <Avatar class="size-8 shrink-0 border border-default bg-white">
                  <AvatarImage v-if="listing.projectLogo" :src="listing.projectLogo" :alt="listing.ticker" />
                  <AvatarFallback class="text-2xs">
                    {{ listing.ticker.slice(0, 1) }}
                  </AvatarFallback>
                </Avatar>
                <div class="flex min-w-0 flex-col gap-1">
                  <span class="truncate text-sm font-semibold text-primal">{{ listing.ticker }}</span>
                  <span class="truncate text-xs text-secondary">
                    {{ listing.exchangeName }}: {{ formatValue(listing.day1VolumeUsd, 'currency:short,0') }} day-1 trade volume
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between gap-2 sm:flex-col sm:items-end sm:gap-1">
              <span class="text-2xs text-secondary">7D Price Performance</span>
              <div class="inline-flex items-center gap-2">
                <span
                  class="font-mono text-sm font-semibold"
                  :class="listing.pricePerformance7d >= 0 ? 'text-success' : 'text-error'"
                >
                  {{ formatSignedPercent(listing.pricePerformance7d, 1) }}
                </span>
                <div class="h-6 w-16 shrink-0">
                  <ChartSparkline
                    v-if="listing.trend.length"
                    :data="listing.trend"
                    x-axis="x"
                    y-axis="y"
                    :color="listing.pricePerformance7d >= 0 ? SPARKLINE_UP_COLOR : SPARKLINE_DOWN_COLOR"
                    :fill-opacity="0.08"
                    width="100%"
                    height="24"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </CardContent>
  </Card>
</template>
